/**
 * 板情報・連系線空容量パネル (`IntradayBoardInterconPanel`) 用のデータ取得 + 整形。
 *
 * 板発注タブ (`MarketBoardTab`) と入札追加タブ (`BidCreateTab`) の両方が
 * `useMarketOrderBook` + `useMarketInterconnectionCapacities` の 2 RPC を同じ形に
 * 整形して消費するため、 この 1 箇所に集約する (= 対象コマ・データ取得の変更が
 * 2 ファイルへ複製されるのを防ぐ)。 対象コマの決定 (板発注: 選択中コマ / 入札追加:
 * まだ入札できる最も早いコマ) は呼び出し側の責務のまま残す (2 タブで意味が異なるため)。
 */
import { useCallback, useMemo } from 'react'

import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import type { BiddingInterconnectionCapacity } from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'
import {
  listBiddingInterconnectionCapacities,
  listBiddingMarketOrderBook,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_market_service-BiddingIntradayMarketService_connectquery'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useQueryClient } from '@tanstack/react-query'

import { useMarketInterconnectionCapacities } from '~/pages/IntradayBidding/hooks/useMarketInterconnectionCapacities'
import { useMarketOrderBook } from '~/pages/IntradayBidding/hooks/useMarketOrderBook'
import { filterBidsByAreaGroups } from '~/pages/IntradayBidding/models/boardAreaGroupFilter'
import {
  MARKET_AREA_GROUPS,
  type MarketAreaGroup,
  type MarketBoardRow,
  pivotMarketBoard,
} from '~/pages/IntradayBidding/models/marketBoardPivot'

type IntradayBoardInterconData = {
  boardRows: MarketBoardRow[]
  totalSellVolume: string | undefined
  totalBuyVolume: string | undefined
  isLoadingBook: boolean
  isErrorBook: boolean
  /** 板発注タブの一次切り分け用 console 出力 (§4 参照) に使う生 error。 */
  errorBook: ConnectError | null
  interconLines: readonly BiddingInterconnectionCapacity[]
  interconUpdatedAt: Timestamp | undefined
  isLoadingIntercon: boolean
  isErrorIntercon: boolean
  errorIntercon: ConnectError | null
  /**
   * cache 済みの板・連系線を stale にする (refetch はしない)。 `enabled` を false → true に
   * 戻す直前に呼ぶと、 無効の間に購読していなかった更新を再有効化時の取得で拾える
   * (query は staleTime: Infinity のため、 呼ばないと古い cache のまま表示される)。
   */
  markStale: () => void
}

/**
 * `areaGroups` は板に出すエリアグループ (板発注タブのエリアグループ絞り込み、 AC-BIN-103)。
 * 板の価格帯・合計 MW・最良気配は、 このグループの注文だけで出し直す。 未指定は全 5 グループ
 * (入札追加タブの参照窓は絞り込みを持たない)。
 */
export function useIntradayBoardIntercon(
  deliveryDate: ProtoDate | undefined,
  timeCode: number,
  enabled: boolean,
  areaGroups: readonly MarketAreaGroup[] = MARKET_AREA_GROUPS,
): IntradayBoardInterconData {
  const {
    data: orderBookData,
    isLoading: isLoadingBook,
    isError: isErrorBook,
    error: errorBook,
  } = useMarketOrderBook(deliveryDate, timeCode, enabled)

  const {
    data: interconnData,
    isLoading: isLoadingIntercon,
    isError: isErrorIntercon,
    error: errorIntercon,
  } = useMarketInterconnectionCapacities(deliveryDate, timeCode, enabled)

  const queryClient = useQueryClient()
  const markStale = useCallback(() => {
    if (deliveryDate === undefined || timeCode <= 0) return
    const input = { deliveryDate, timeCode }
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBiddingMarketOrderBook,
        cardinality: 'finite',
        input,
      }),
      refetchType: 'none',
    })
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBiddingInterconnectionCapacities,
        cardinality: 'finite',
        input,
      }),
      refetchType: 'none',
    })
  }, [queryClient, deliveryDate, timeCode])

  const boardRows = useMemo(
    () =>
      pivotMarketBoard(
        filterBidsByAreaGroups(
          orderBookData?.orderBook?.bids ?? [],
          areaGroups,
        ),
      ),
    [orderBookData, areaGroups],
  )

  return {
    boardRows,
    totalSellVolume: orderBookData?.orderBook?.totalSellVolume?.value,
    totalBuyVolume: orderBookData?.orderBook?.totalBuyVolume?.value,
    isLoadingBook,
    isErrorBook,
    errorBook,
    interconLines: interconnData?.capacities?.lines ?? [],
    interconUpdatedAt: interconnData?.capacities?.updatedAt,
    isLoadingIntercon,
    isErrorIntercon,
    errorIntercon,
    markStale,
  }
}
